import React, { useEffect, useState } from "react";
import { Spin, Table } from "antd";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import SidebarNav from "../sidebar";
import { Link, useHistory } from "react-router-dom";
import backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import InputLabel from "@mui/material/InputLabel";
import { Button, TextField, Tooltip } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  addEdd,
  addUser,
  deleteUser,
  reactivateUser,
  deactivateUser,
  activateUser,
  adminDisable2FA,
  editUser,
  getUser,
  getUserById,
  blockUserWithReason,
} from "../../../service/api";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import actions from "../../../redux/user/actions";
import { FaArrowLeft } from "react-icons/fa";
import DateRangePicker from "react-bootstrap-daterangepicker";
import CustomPagination from "../Pagination/pagination";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import Select from "react-select";
import moment from "moment";
import { sanitizeInput } from "../../../config/validation";
import { HiDotsVertical } from "react-icons/hi";
import { triggerDownload } from "../../../config/downloadUtils";

const formatDateTime = (val) =>
  val ? moment(val).format("DD/MM/YYYY HH:mm") : "-";

const formatDate = (val) => (val ? moment(val).format("DD/MM/YYYY") : "-");

const riskLabel = (score) => {
  if (score === undefined || score === null || score === "") return "-";
  if (score > 7) return "High Risk";
  if (score < 4) return "Low Risk";
  return "Medium Risk";
};

const BANK_STATUS_COLORS = {
  APPROVED: { bg: "#e6faf0", color: "#0a7a3b", border: "#0ECB81" },
  PENDING: { bg: "#fff8e1", color: "#a06900", border: "#F0B90B" },
  REJECTED: { bg: "#fee2e7", color: "#a8001f", border: "#F6465D" },
  NOT_ADDED: { bg: "#f1f3f5", color: "#555", border: "#ced4da" },
};

const renderBankStatusTag = (status) => {
  const key = status || "NOT_ADDED";
  const c = BANK_STATUS_COLORS[key] || BANK_STATUS_COLORS.NOT_ADDED;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
      }}
    >
      {key.replace("_", " ")}
    </span>
  );
};

// KYC status colors — reuse the same palette as bank tags + a blue for the
// in-progress states so users can scan the table quickly.
const KYC_STATUS_COLORS = {
  APPROVED:    { bg: "#e6faf0", color: "#0a7a3b", border: "#0ECB81", label: "Approved" },
  PENDING:     { bg: "#fff8e1", color: "#a06900", border: "#F0B90B", label: "Pending" },
  "IN-PROCESS":{ bg: "#eef4ff", color: "#1a3fbd", border: "#3b66d6", label: "In Progress" },
  REJECTED:    { bg: "#fee2e7", color: "#a8001f", border: "#F6465D", label: "Rejected" },
  NOT_STARTED: { bg: "#f1f3f5", color: "#555",    border: "#ced4da", label: "Not Started" },
};

const renderKycStatusTag = (status) => {
  const key = status || "NOT_STARTED";
  const c = KYC_STATUS_COLORS[key] || KYC_STATUS_COLORS.NOT_STARTED;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
      }}
    >
      {c.label}
    </span>
  );
};

// ComplyAdvantage badge — shows whether the user has a comply record linked.
const renderComplyAdvantageTag = (complyId) => {
  const linked = !!complyId && String(complyId).trim() !== "";
  return (
    <span
      title={linked ? `Comply ID: ${complyId}` : "No ComplyAdvantage record"}
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        background: linked ? "#e6faf0" : "#f1f3f5",
        color: linked ? "#0a7a3b" : "#666",
        border: `1px solid ${linked ? "#0ECB81" : "#ced4da"}`,
      }}
    >
      {linked ? "Linked" : "Not Linked"}
    </span>
  );
};

// Each column: key (stable id), title, defaultVisible, render(record)
// Action column is always visible and is appended separately.
const ALL_COLUMNS = [
  {
    key: "user",
    title: "User",
    defaultVisible: true,
    render: (r) => `${r?.firstName ?? ""} ${r?.lastName ?? ""}`.trim() || "-",
  },
  {
    key: "email",
    title: "Email",
    defaultVisible: true,
    render: (r) => r?.email || "-",
  },
  {
    key: "phoneNumber",
    title: "Phone Number",
    defaultVisible: true,
    render: (r) => r?.phoneNumber || "-",
  },
  {
    key: "kycStatus",
    title: "KYC Status",
    defaultVisible: true,
    render: (r) => renderKycStatusTag(r?.kycStatus),
  },
  {
    key: "complyAdvantage",
    title: "ComplyAdvantage",
    defaultVisible: false,
    render: (r) => renderComplyAdvantageTag(r?.complyAdvantage),
  },
  {
    key: "bankVerificationStatus",
    title: "Bank Verification",
    defaultVisible: true,
    render: (r) => renderBankStatusTag(r?.bankVerificationStatus),
  },
  {
    key: "bankVerificationStatus",
    title: "Bank Verification",
    defaultVisible: true,
    render: (r) => renderBankStatusTag(r?.bankVerificationStatus),
  },
  {
    key: "kycRetryCount",
    title: "KYC Count",
    defaultVisible: true,
    render: (r) =>
      r?.kycRetryCount !== undefined && r?.kycRetryCount !== null
        ? r.kycRetryCount
        : "-",
  },
  {
    key: "kycSubmittedOn",
    title: "KYC Submitted On",
    defaultVisible: true,
    render: (r) => formatDateTime(r?.kyc?.isSubmittedOn),
  },
  {
    key: "riskAssessment",
    title: "Risk Assessment",
    defaultVisible: true,
    render: (r) => riskLabel(r?.riskAssessment?.score),
  },
  {
    key: "createdAt",
    title: "Created At",
    defaultVisible: true,
    render: (r) => formatDateTime(r?.createdAt),
  },

  // Hidden by default — extra fields available from the backend model
  {
    key: "userId",
    title: "User ID",
    defaultVisible: false,
    render: (r) => r?.userId || r?._id || "-",
  },
  {
    key: "userName",
    title: "Username",
    defaultVisible: false,
    render: (r) => r?.userName || "-",
  },
  {
    key: "riskScore",
    title: "Risk Score",
    defaultVisible: false,
    render: (r) =>
      r?.riskAssessment?.score !== undefined &&
      r?.riskAssessment?.score !== null
        ? r.riskAssessment.score
        : "-",
  },
  {
    key: "country",
    title: "Country",
    defaultVisible: false,
    render: (r) => r?.country || "-",
  },
  {
    key: "countryCode",
    title: "Country Code",
    defaultVisible: false,
    render: (r) => r?.kyc?.countryOfResidance || r?.country || "-",
  },
  {
    key: "nationality",
    title: "Nationality",
    defaultVisible: false,
    render: (r) => r?.nationality || r?.kyc?.nationality || "-",
  },
  {
    key: "dob",
    title: "Date of Birth",
    defaultVisible: false,
    render: (r) => formatDate(r?.dob || r?.kyc?.dob),
  },
  {
    key: "gender",
    title: "Gender",
    defaultVisible: false,
    render: (r) => r?.kyc?.gender || "-",
  },
  {
    key: "pan",
    title: "PAN",
    defaultVisible: false,
    render: (r) => r?.kyc?.pan || "-",
  },
  {
    key: "aadhar",
    title: "Aadhar",
    defaultVisible: false,
    render: (r) => r?.kyc?.aadhar || "-",
  },
  {
    key: "selfieConfidence",
    title: "Selfie Confidence",
    defaultVisible: false,
    render: (r) =>
      r?.kyc?.selfieConfidence !== undefined &&
      r?.kyc?.selfieConfidence !== null
        ? r.kyc.selfieConfidence
        : "-",
  },
  {
    key: "addressState",
    title: "State",
    defaultVisible: false,
    render: (r) => r?.address?.state || r?.kyc?.state || "-",
  },
  {
    key: "addressCity",
    title: "City",
    defaultVisible: false,
    render: (r) => r?.address?.city || "-",
  },
  {
    key: "pincode",
    title: "Pincode",
    defaultVisible: false,
    render: (r) => r?.address?.pinCode || r?.kyc?.pincode || "-",
  },
  {
    key: "occupation",
    title: "Occupation",
    defaultVisible: false,
    render: (r) => r?.occupation || "-",
  },
  {
    key: "income",
    title: "Income",
    defaultVisible: false,
    render: (r) => r?.income || "-",
  },
  {
    key: "pep",
    title: "PEP",
    defaultVisible: false,
    render: (r) => r?.pep || "-",
  },
  {
    key: "edd",
    title: "EDD",
    defaultVisible: false,
    render: (r) => (r?.edd ? "Yes" : "No"),
  },
  {
    key: "eddStatus",
    title: "EDD Status",
    defaultVisible: false,
    render: (r) => r?.eddStatus || "-",
  },
  {
    key: "isBlock",
    title: "Block Status",
    defaultVisible: false,
    render: (r) => (r?.isBlock ? "Blocked" : "Active"),
  },
  {
    key: "accountStatus",
    title: "Account Status",
    defaultVisible: false,
    render: (r) => r?.accountStatus || "-",
  },
  {
    key: "isEmailVerified",
    title: "Email Verified",
    defaultVisible: false,
    render: (r) => (r?.isEmailVerified ? "Yes" : "No"),
  },
  {
    key: "isMobileVerified",
    title: "Mobile Verified",
    defaultVisible: false,
    render: (r) => (r?.isMobileVerified ? "Yes" : "No"),
  },
  {
    key: "enabled2fa",
    title: "2FA",
    defaultVisible: false,
    render: (r) => (r?.enabled2fa ? "Enabled" : "Disabled"),
  },
  {
    key: "bankName",
    title: "Bank Name",
    defaultVisible: false,
    render: (r) => r?.bankName || "-",
  },
  {
    key: "bankAccountNumber",
    title: "Bank A/C No.",
    defaultVisible: false,
    render: (r) => r?.bankAccountNumber || "-",
  },
  {
    key: "bankIfsc",
    title: "IFSC",
    defaultVisible: false,
    render: (r) => r?.bankIfsc || "-",
  },
  {
    key: "refral",
    title: "Referral Code",
    defaultVisible: false,
    render: (r) => r?.refral || "-",
  },
  {
    key: "updatedAt",
    title: "Updated At",
    defaultVisible: false,
    render: (r) => formatDateTime(r?.updatedAt),
  },
];

// Keys that should be visible by default in "User Alerts" mode
const ALERT_EXTRA_KEYS = ["countryCode", "addressState", "addressCity", "riskScore", "selfieConfidence", "isBlock"];

const styleDelete = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 290,
  background: "white",
  // border: '1px solid #000',
  borderRadius: "10px",
  boxShadow: 24,
  p: 4,
  outline: "none",
};

const kycOptions = [
  { label: "Approved", value: "APPROVED" },
  { label: "Pending", value: "PENDING" },
  { label: "In-progress", value: "IN-PROCESS" },
  { label: "Rejected", value: "REJECTED" },
];

const bankVerificationOptions = [
  { label: "Approved", value: "APPROVED" },
  { label: "Pending Review", value: "PENDING" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Not Added", value: "NOT_ADDED" },
];

const userStatusOptions = [
  { label: "All User", value: "" },
  { label: "Blocked User", value: "block" },
  { label: "Deleted User", value: "deleted" },
];

const complyAdvantageOptions = [
  { label: "Linked", value: "true" },
  { label: "Not Linked", value: "false" },
];

const freezeOptions = [
  { label: "Frozen", value: "true" },
  { label: "Active", value: "false" },
];

const riskOptions = [
  { label: "High", value: "High" },
  { label: "Medium", value: "Medium" },
  { label: "Low", value: "Low" },
];

// Inline styles for react-select
const customStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    height: "40px",
    width: "200px",
    minWidth: "120px",
    borderRadius: "4px",
    border: "1px solid #ced4da",
    fontSize: "14px",
    padding: "0px 4px",
    backgroundColor: "#fff",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

const User = () => {
  const [riskOption, setRiskOption] = useState("");
  const [filter, setFilter] = useState("");
  const [kyc, setKyc] = useState("");
  const [bankStatus, setBankStatus] = useState("");
  const [complyFilter, setComplyFilter] = useState(""); // "" | "true" | "false"
  const [freezeFilter, setFreezeFilter] = useState(""); // "" | "true" | "false"
  const [fromRiskScore, setFromRiskScore] = useState();
  const [toRiskScore, setToRiskScore] = useState();
  const [roleInput, setroleInput] = useState({
    phoneNumber: "",
    email: "",
    firstName: "",
    lastName: "",
    riskAssessment: {},
  });
  const [roleInputError, setroleInputError] = useState({
    phoneNumber: false,
    email: false,
    firstName: false,
    lastName: false,
  });
  const [userList, setUserlist] = useState([]);
  const [emailValidation, setEmailValidation] = useState(false);
  const [user, setUser] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const handleOpenDelete = () => setOpenDelete(true);
  const handleCloseDelete = () => setOpenDelete(false);
  const [openReactivate, setOpenReactivate] = useState(false);
  const handleOpenReactivate = () => setOpenReactivate(true);
  const handleCloseReactivate = () => setOpenReactivate(false);
  const [openDisable2FA, setOpenDisable2FA] = useState(false);
  const handleOpenDisable2FA = () => setOpenDisable2FA(true);
  const handleCloseDisable2FA = () => setOpenDisable2FA(false);

  const [openDeactivate, setOpenDeactivate] = useState(false);
  const handleOpenDeactivate = () => setOpenDeactivate(true);
  const handleCloseDeactivate = () => setOpenDeactivate(false);

  // Block with reason modal
  const [openBlock, setOpenBlock] = useState(false);
  const [blockAction, setBlockAction] = useState(true); // true = blocking, false = unblocking
  const [blockReason, setBlockReason] = useState("");
  const [blockReasonError, setBlockReasonError] = useState(false);

  // User alerts mode
  const [alertMode, setAlertMode] = useState(false);
  const [id, setId] = useState(false);
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [filterUserId, setFilterUserId] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [page, setPage] = useState(1);
  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "user-list",
    allColumns: ALL_COLUMNS,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const history = useHistory();
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const roleChange = (e) => {
    const { name, value } = e.target;
    if (e.target.type === "number") {
      if (value >= 0) {
        setroleInput({ ...roleInput, [name]: sanitizeInput(value) });
      }
    } else {
      setroleInput({ ...roleInput, [name]: sanitizeInput(value) });
    }
    setroleInputError({ ...roleInputError, [name]: false });
  };

  const handleRiskOptionChange = (event) => {
    if (!event) {
      setRiskOption("");
      setFromRiskScore(undefined);
      setToRiskScore(undefined);
      return;
    }
    const value = event.value;
    setRiskOption(value);

    if (value === "High") {
      setFromRiskScore(7);
      setToRiskScore(undefined);
    } else if (value === "Low") {
      setFromRiskScore(undefined);
      setToRiskScore(3);
    } else if (value === "Medium") {
      setFromRiskScore(4);
      setToRiskScore(6);
    } else {
      setFromRiskScore(undefined);
      setToRiskScore(undefined);
    }
  };

  const getUserList = () => {
    let body = {
      page: page,
      sizePerPage: pageSize,
      pagination: true,
    };
    const searchValue = filterEmail || searchTerm;
    if (searchValue) {
      body.search = searchValue;
    }
    if (kyc) {
      body.kycStatus = kyc;
    }
    if (bankStatus) {
      body.bankVerificationStatus = bankStatus;
    }
    if (complyFilter) {
      body.complyAdvantage = complyFilter; // "true" / "false"
    }
    if (freezeFilter) {
      body.isFrozen = freezeFilter; // "true" / "false"
    }
    if (startDate) {
      body.startDate = startDate;
    }
    if (endDate) {
      body.endDate = endDate;
    }
    if (fromRiskScore !== undefined) {
      body.fromRiskScore = fromRiskScore;
    }
    if (toRiskScore !== undefined) {
      body.toRiskScore = toRiskScore;
    }

    if (filter === "block") {
      body.block = true;
    } else if (filter === "deleted") {
      body.isDeleted = true;
    }
    if (alertMode) {
      body.alert = true;
    }
    if (filterUserId) {
      body.userId = filterUserId;
    }
    if (filterMobile) {
      body.mobileNumber = filterMobile;
    }
    setLoading(true);
    getUser(body)
      .then((res) => {
        if (res?.status) {
          setUserlist(res?.data);
          dispatch(actions.setUserList(res?.data?.result));
        }
        setLoading(false);
      })
      .finally((res) => {
        setLoading(false);
      });
  };
  const handleDownload = (format) => {
    const body = { pagination: false };
    const searchValue = filterEmail || searchTerm;
    if (searchValue) body.search = searchValue;
    if (kyc) body.kycStatus = kyc;
    if (bankStatus) body.bankVerificationStatus = bankStatus;
    if (complyFilter) body.complyAdvantage = complyFilter;
    if (freezeFilter) body.isFrozen = freezeFilter;
    if (startDate) body.startDate = startDate;
    if (endDate) body.endDate = endDate;
    if (fromRiskScore !== undefined) body.fromRiskScore = fromRiskScore;
    if (toRiskScore !== undefined) body.toRiskScore = toRiskScore;
    if (filter === "block") body.block = true;
    else if (filter === "deleted") body.isDeleted = true;
    if (filterUserId) body.userId = filterUserId;
    if (filterMobile) body.mobileNumber = filterMobile;
    setDownloadLoading(true);
    getUser(body)
      .then((res) => {
        const result = res?.data?.result ?? [];
        if (!result.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
        const cols = ALL_COLUMNS.filter((c) => visibleColumnKeys.includes(c.key));
        triggerDownload(format, result, cols, "users");
      })
      .finally(() => setDownloadLoading(false));
  };

  const regEx = /^[a-zA-Z0-9]+@(?:[a-zA-Z0-9]+\.)+[A-Za-z]+$/;
  const handleValidate = (e) => {
    if (roleInput?.email) {
      if (regEx.test(e)) {
        setEmailValidation(false);
      } else {
        setEmailValidation(true);
      }
    }
  };
  const handleClear = () => {
    setroleInput({
      ...roleInput,
      phoneNumber: "",
      email: "",
      firstName: "",
      lastName: "",
    });
    setroleInputError({
      phoneNumber: false,
      email: false,
      firstName: false,
      lastName: false,
    });
  };
  // ADD USER FUNCTION
  const addUserFun = () => {
    if (
      !roleInput?.phoneNumber &&
      !roleInput?.email &&
      !roleInput?.firstName &&
      !roleInput?.lastName
    ) {
      setroleInputError({
        ...roleInputError,
        phoneNumber: true,
        email: true,
        firstName: true,
        lastName: true,
      });
      return;
    } else if (!roleInput?.firstName) {
      setroleInputError({ ...roleInputError, firstName: true });
      return;
    } else if (!roleInput?.lastName) {
      setroleInputError({ ...roleInputError, lastName: true });
      return;
    } else if (!roleInput?.email) {
      setroleInputError({ ...roleInputError, email: true });
      return;
    } else if (!roleInput?.phoneNumber) {
      setroleInputError({ ...roleInputError, phoneNumber: true });
      return;
    }
    if (roleInput && !emailValidation) {
      setLoading(true);
      if (!edit) {
        let obj = {
          firstName: roleInput?.firstName,
          email: roleInput?.email,
          phoneNumber: roleInput?.phoneNumber,
          lastName: roleInput?.lastName,
        };
        addUser(obj)
          .then((res) => {
            if (res?.status) {
              toast.success(res?.message, { toastId: 'user-update-success' });
              setUser(false);
              getUserList();
              handleClear();
            } else {
              toast.error(res?.message, { toastId: 'user-update-error' });
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message, { toastId: 'user-update-error' });
          })
          .finally((res) => {
            setLoading(false);
          });
      } else {
        let obj = {
          firstName: roleInput?.firstName,
          email: roleInput?.email,
          phoneNumber: roleInput?.phoneNumber,
          lastName: roleInput?.lastName,
        };
        editUser(id?._id, obj)
          .then((res) => {
            if (res?.status) {
              toast.success(res?.message, { toastId: 'user-update-success' });
              setEdit(false);
              setUser(false);
              getUserList();
              handleClear();
            } else {
              toast.error(res?.message, { toastId: 'user-update-error' });
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message, { toastId: 'user-update-error' });
          })
          .finally((res) => {
            setLoading(false);
          });
      }
    }
  };
  // DELETE USER FUNCTION

  const handleDeleteFunction = () => {
    setLoading(true);
    deleteUser(id?._id)
      .then((res) => {
        if (res?.status) {
          toast.success("User deleted successfully", { toastId: 'user-delete-success' });
          handleCloseDelete();
          getUserList();
          setId(null);
        } else {
          toast.error(res?.message, { toastId: 'user-delete-error' });
        }
      })
      .catch((e) => {
        setLoading(false);
        toast.error(e?.response?.data?.message, { toastId: 'user-delete-error' });
      })
      .finally((res) => {
        setLoading(false);
      });
  };
  useEffect(() => {
    getUserList();
  }, [
    page,
    pageSize,
    startDate,
    endDate,
    fromRiskScore,
    toRiskScore,
    filter,
    searchTerm,
    kyc,
    bankStatus,
    complyFilter,
    freezeFilter,
    filterUserId,
    filterMobile,
    filterEmail,
    alertMode,
  ]);

  // Reset to page 1 whenever filters or page size change so the user
  // does not end up on an empty trailing page.
  useEffect(() => {
    setPage(1);
  }, [
    pageSize,
    startDate,
    endDate,
    fromRiskScore,
    toRiskScore,
    filter,
    searchTerm,
    kyc,
    bankStatus,
    complyFilter,
    freezeFilter,
    filterUserId,
    filterMobile,
    filterEmail,
    alertMode,
  ]);

  const getUserDataById = (id) => {
    getUserById(id)
      ?.then((res) => {
        if (res?.status) {
          let data = res?.data;
          setroleInput({
            ...roleInput,
            firstName: data?.firstName,
            lastName: data?.lastName,
            email: data?.email,
            phoneNumber: data?.phoneNumber,
            riskAssessment: data?.riskAssessment,
          });
        }
      })
      .catch((e) => {});
  };
  // useEffect(() => {
  //   if (edit) {
  //     let newtag = userList?.docs?.filter((item) => item._id === id?._id);
  //
  //     // setroleInput({
  //     //   ...roleInput,
  //     //   name: data?.name,
  //     //   email: data?.email,
  //     //   password: data?.password,
  //     //   role: data?.role,
  //     // });
  //   } else {
  //     handleClear();
  //   }
  // }, [edit]);

  const handleEdd = (id) => {
    if (loading) {
      return;
    }
    setLoading(true);

    addEdd(id?._id)
      ?.then((res) => {
        if (res?.status) {
          getUserList();
          setId(null);
          toast.success(res?.message, { toastId: 'user-update-success' });
        } else {
          toast.error(res?.message, { toastId: 'user-update-error' });
        }
      })
      ?.catch((e) => {
        toast.error(e?.response?.data?.message, { toastId: 'user-update-error' });
      })
      ?.finally(() => {
        setLoading(false);
      });
  };
  const openBlockModal = (isBlocking) => {
    setBlockAction(isBlocking);
    setBlockReason("");
    setBlockReasonError(false);
    setOpenBlock(true);
  };

  const handleBlockConfirm = () => {
    if (blockAction && !blockReason.trim()) {
      setBlockReasonError(true);
      return;
    }
    if (loading) return;
    setLoading(true);
    blockUserWithReason(id?._id, { block: blockAction, reason: blockReason.trim() })
      ?.then((res) => {
        if (res?.status) {
          getUserList();
          setId(null);
          setOpenBlock(false);
          setBlockReason("");
          toast.success(res?.message, { toastId: 'user-block-success' });
        } else {
          toast.error(res?.message, { toastId: 'user-block-error' });
        }
      })
      ?.catch((e) => {
        toast.error(e?.response?.data?.message || "Failed to update block status", { toastId: 'user-block-error' });
      })
      ?.finally(() => setLoading(false));
  };

  const handleReactivateFunction = () => {
    if (loading) return;
    setLoading(true);
    reactivateUser(id?._id)
      ?.then((res) => {
        if (res?.status) {
          getUserList();
          setId(null);
          handleCloseReactivate();
          toast.success(res?.message, { toastId: 'user-update-success' });
        } else {
          toast.error(res?.message, { toastId: 'user-update-error' });
        }
      })
      ?.catch((e) => {
        toast.error(e?.response?.data?.message, { toastId: 'user-update-error' });
      })
      ?.finally(() => setLoading(false));
  };

  const handleDeactivateConfirm = () => {
    if (loading) return;
    setLoading(true);
    const apiCall = id?.isDeactivated
      ? activateUser(id?._id)
      : deactivateUser(id?._id);
    apiCall
      ?.then((res) => {
        if (res?.status) {
          getUserList();
          setId(null);
          handleCloseDeactivate();
          toast.success(res?.message, { toastId: 'user-update-success' });
        } else {
          toast.error(res?.message, { toastId: 'user-update-error' });
        }
      })
      ?.catch((e) => {
        toast.error(e?.response?.data?.message, { toastId: 'user-update-error' });
      })
      ?.finally(() => setLoading(false));
  };

  const handleDisable2FAFunction = () => {
    if (loading) return;
    setLoading(true);
    adminDisable2FA(id?._id)
      ?.then((res) => {
        if (res?.status) {
          getUserList();
          setId(null);
          handleCloseDisable2FA();
          toast.success(res?.message, { toastId: 'user-update-success' });
        } else {
          toast.error(res?.message, { toastId: 'user-update-error' });
        }
      })
      ?.catch((e) => {
        toast.error(e?.response?.data?.message, { toastId: 'user-update-error' });
      })
      ?.finally(() => setLoading(false));
  };

  const actionColumn = {
    key: "__action",
    title: "Action",
    className: "text-end",
    render: (_, row) => (
      <div
        className="relative inline-block text-left"
        onClick={(e) => e.stopPropagation()}
      >
          {/* <div className="text-end">
              {!row?.edd ? (
                <a
                  className="me-1 btn btn-sm bg-primary-light "
                  onClick={() => {
                    handleEdd(row?._id);
                  }}
                >
                  Ask for EDD
                </a>
              ) : (
                <a className="me-1 btn btn-sm bg-primary-light ">
                  Already in EDD
                </a>
              )}
              {!row?.isBlock ? (
                <a
                  className="me-1 btn btn-sm bg-danger-light "
                  onClick={() => {
                    setId(row);
                    openBlockModal(true);
                  }}
                >
                  Block
                </a>
              ) : (
                <a
                  className="me-1 btn btn-sm bg-success-light"
                  onClick={() => {
                    setId(row);
                    openBlockModal(false);
                  }}
                >
                  Unblock
                </a>
              )}
              <a
                className="me-1 btn btn-sm bg-success-light "
                onClick={() => {
                  setId(row);
                  handleClear();
                  setUser(true);
                  setEdit(true);
                  getUserDataById(row?._id);
                }}
              >
                <i className="fe fe-pencil"></i> Edit
              </a>
  
              <a
                onClick={() => {
                  handleOpenDelete();
                  setId(row);
                }}
                className="me-1 btn btn-sm bg-danger-light"
              >
                <i className="fe fe-trash"></i> Delete
              </a>
            </div> */}

          <Button
            id="basic-button"
            aria-controls={open ? row : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={(e) => {
              setId(row);
              handleClick(e);
            }}
            style={{ minWidth: 0, padding: 6 }}
            className="rounded-full hover:bg-gray-100"
          >
            <HiDotsVertical size={18} />
          </Button>

          <Menu
            id={row}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              list: {
                "aria-labelledby": "basic-button",
                className: "py-2 px-1 min-w-[180px]",
              },
            }}
            PaperProps={{
              elevation: 4,
              sx: {
                borderRadius: 2,
                mt: 1,
                boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            {!id?.edd ? (
              <MenuItem
                onClick={() => {
                  handleEdd(id);
                  handleClose();
                }}
                className="hover:bg-gray-100 text-sm px-4"
              >
                Ask for EDD
              </MenuItem>
            ) : (
              <MenuItem className="text-sm px-4 text-gray-400">
                Already in EDD
              </MenuItem>
            )}

            {!id?.isBlock ? (
              <MenuItem
                onClick={() => {
                  openBlockModal(true);
                  handleClose();
                }}
                className="hover:bg-gray-100 text-sm px-4 text-red-600"
              >
                Block
              </MenuItem>
            ) : (
              <MenuItem
                onClick={() => {
                  openBlockModal(false);
                  handleClose();
                }}
                className="hover:bg-gray-100 text-sm px-4 text-green-600"
              >
                Unblock
              </MenuItem>
            )}

            <Tooltip
              title={
                id?.isDeactivated
                  ? "Activate this user's account. They will be able to log in again."
                  : "Once deactivated, the user cannot log in. They must raise a support ticket to be reactivated."
              }
              placement="left"
              arrow
            >
              <MenuItem
                onClick={() => {
                  handleOpenDeactivate();
                  handleClose();
                }}
                className={
                  id?.isDeactivated
                    ? "hover:bg-gray-100 text-sm px-4 text-green-600"
                    : "hover:bg-gray-100 text-sm px-4 text-orange-600"
                }
              >
                {id?.isDeactivated ? "Activate" : "Deactivate"}
              </MenuItem>
            </Tooltip>

            <MenuItem
              onClick={() => {
                history.push(`/admin/user/profile/${id?._id}`);
              }}
              className="hover:bg-gray-100 text-sm px-4"
            >
              View
            </MenuItem>
            <MenuItem
              onClick={() => {
                setId(id);
                handleClear();
                setUser(true);
                setEdit(true);
                getUserDataById(id?._id);
                handleClose();
              }}
              className="hover:bg-gray-100 text-sm px-4"
            >
              Edit
            </MenuItem>

            {id?.isDeleted ? (
              <MenuItem
                onClick={() => {
                  handleOpenReactivate();
                  handleClose();
                }}
                className="hover:bg-gray-100 text-sm px-4 text-green-600"
              >
                Reactivate
              </MenuItem>
            ) : (
              <MenuItem
                onClick={() => {
                  handleOpenDelete();
                  handleClose();
                }}
                className="hover:bg-gray-100 text-sm px-4 text-red-600"
              >
                Delete
              </MenuItem>
            )}

            {(id?.enabled2fa || id?.authenticatorVerified) && (
              <MenuItem
                onClick={() => {
                  handleOpenDisable2FA();
                  handleClose();
                }}
                className="hover:bg-gray-100 text-sm px-4 text-orange-600"
              >
                Disable 2FA
              </MenuItem>
            )}
          </Menu>
        </div>
      ),
    };

  // In alert mode, force-include location columns even if not toggled on
  const alertColumns = alertMode
    ? ALL_COLUMNS.filter((c) =>
        dynamicColumns.some((d) => d.key === c.key) || ALERT_EXTRA_KEYS.includes(c.key)
      ).map((c) => ({
        key: c.key,
        title: c.title,
        dataIndex: c.key,
        render: (_, r) => c.render(r),
      }))
    : dynamicColumns;

  const columns2 = [...alertColumns, actionColumn];

  const handleDateRangeChange = (event, picker) => {
    setStartDate(picker.startDate.format("YYYY-MM-DD"));
    setEndDate(picker.endDate.format("YYYY-MM-DD"));
  };
  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-7 col-aut0">
                <h3 className="page-title" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {alertMode ? "User Alerts" : "User"}
                  <button
                    onClick={() => setAlertMode((v) => !v)}
                    style={{
                      fontSize: 12, fontWeight: 600, padding: "3px 12px", borderRadius: 20,
                      border: "none", cursor: "pointer",
                      background: alertMode ? "#b71c1c" : "#fff3e0",
                      color: alertMode ? "#fff" : "#e65100",
                    }}
                  >
                    {alertMode ? "✕ Exit Alerts" : "⚠ User Alerts"}
                  </button>
                </h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">{alertMode ? "User Alerts" : "User"}</li>
                </ul>
              </div>
              <div className="col-sm-5 col">
                {/* {permission &&
                  permission?.length > 0 &&
                  permission?.find((module) => module.module === "User")
                    ?.create &&
                  !user && (
                    <a
                      onClick={() => {
                        handleClear(), setEdit(false), setUser(true);
                      }}
                      data-bs-toggle="modal"
                      className="btn btn-primary float-end mt-2"
                    >
                      Add User
                    </a>
                  )} */}

                {/* <Modal
                  aria-labelledby="transition-modal-title"
                  aria-describedby="transition-modal-description"
                  open={User}
                  onClose={handleRoleClose}
                  closeAfterTransition
                  slots={{ backdrop: backdrop }}
                  slotProps={{
                    backdrop: {
                      timeout: 500,
                    },
                  }}
                >
                  <Fade in={User}>
                    <Box style={style}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "10px 13px 0px",
                          borderBottom: "1px solid grey",
                        }}
                      >
                        <h4>{edit ? "Edit User" : "Add User"}</h4>
                        <AiOutlineClose
                          style={{ marginTop: "3px", cursor: "pointer" }}
                          onClick={() => {
                            handleRoleClose(), setEdit(false);
                          }}
                        />
                      </div>
                      <div style={{ margin: "10px" }}>
                        <InputLabel
                          id="demo-simple-select-label"
                          style={{ marginLeft: "7px" }}
                        >
                          User Name &nbsp;
                          {roleInputError?.name && (
                            <small style={{ color: "red" }}>
                              {"Username is required"}
                            </small>
                          )}
                        </InputLabel>
                        <TextField
                          variant="outlined"
                          style={{ width: "370px", marginLeft: "5px" }}
                          className="form-control"
                          type="text"
                          placeholder="User Name"
                          value={roleInput?.name}
                          onChange={roleChange}
                          name="name"
                          id="name"
                        />
                        <InputLabel
                          id="demo-simple-select-label"
                          style={{ marginTop: "20px", marginLeft: "7px" }}
                        >
                          Email &nbsp;
                          {emailValidation ? (
                            <small style={{ color: "red" }}>
                              {"Enter Valid Email"}
                            </small>
                          ) : (
                            roleInputError?.email && (
                              <small style={{ color: "red" }}>
                                {"Email is required"}
                              </small>
                            )
                          )}
                        </InputLabel>
                        <TextField
                          variant="outlined"
                          style={{ width: "370px", marginLeft: "5px" }}
                          className="form-control"
                          type="email"
                          placeholder="abc@samsar.com"
                          value={roleInput?.email}
                          // onChange={roleChange}
                          InputProps={{
                            readOnly: true,
                          }}
                          onBlur={(e) => handleValidate(e.target.value)}
                          name="email"
                          id="email"
                        />
                        {!edit ? (
                          <>
                            <InputLabel
                              id="demo-simple-select-label"
                              style={{ marginTop: "20px", marginLeft: "7px" }}
                            >
                              Password &nbsp;
                              {roleInputError?.password && (
                                <small style={{ color: "red" }}>
                                  {"password is required"}
                                </small>
                              )}
                            </InputLabel>
                            <TextField
                              variant="outlined"
                              style={{ width: "370px", marginLeft: "5px" }}
                              className="form-control"
                              type="password"
                              placeholder="******"
                              value={roleInput?.password}
                              onChange={roleChange}
                              name="password"
                              id="password"
                            />
                          </>
                        ) : (
                          ""
                        )}
                        <InputLabel
                          id="demo-simple-select-label"
                          style={{ marginTop: "20px", marginLeft: "7px" }}
                        >
                          Role
                        </InputLabel>
                        <Select
                          labelId="test-select-label"
                          id="test-select"
                          value={roleId}
                          label="Role"
                          onChange={handleChange}
                          style={{ width: "370px", marginLeft: "5px" }}
                        >
                          {rolelist &&
                            rolelist?.map((item, _id) => {
                              return (
                                <MenuItem
                                  key={_id}
                                  style={{ height: "50px" }}
                                  value={item._id}
                                >
                                  {item.role}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </div>
                      <div
                        style={{
                          borderTop: "1px solid grey",
                          height: "56px",
                          display: "flex",
                          justifyContent: "center",
                          marginTop: "30px",
                        }}
                      >
                        <button
                          className="btn btn-primary"
                          onClick={addUserFun}
                          disabled={loading}
                          style={{ marginBottom: "10px", marginTop: "9px" }}
                        >
                          {loading ? (
                            <Spinner size="sm" />
                          ) : edit ? (
                            "Update"
                          ) : (
                            "Add"
                          )}
                        </button>
                      </div>
                    </Box>
                  </Fade>
                </Modal> */}
              </div>
            </div>
          </div>

          {/* /Page Header */}
          <div className="row">
            <div className="col-sm-12">
              {user ? (
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "10px 0px",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                      }}
                    >
                      <FaArrowLeft
                        style={{
                          marginTop: "3px",
                          cursor: "pointer",
                          width: "30px",
                          height: "20px",
                        }}
                        onClick={() => {
                          setEdit(false);
                          setUser(false);
                        }}
                      />
                      <h4>{edit ? "Edit User" : "Add User"}</h4>
                    </div>{" "}
                    <div
                      style={{
                        // borderTop: "1px solid grey",
                        height: "56px",
                        display: "flex",
                        justifyContent: "end",
                        // marginTop: "30px",
                      }}
                    >
                      <button
                        className="btn btn-primary"
                        onClick={addUserFun}
                        disabled={loading}
                        style={{ marginBottom: "10px", marginTop: "9px" }}
                      >
                        {loading ? (
                          <Spinner size="sm" />
                        ) : edit ? (
                          "Update"
                        ) : (
                          "Add"
                        )}
                      </button>
                    </div>
                  </div>
                  <div style={{ margin: "10px" }} className="page-grid">
                    <div>
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        First Name &nbsp;
                        {roleInputError?.firstName && (
                          <small style={{ color: "red" }}>
                            {"First Name is required"}
                          </small>
                        )}
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="text"
                        placeholder="First Name"
                        value={roleInput?.firstName}
                        onChange={roleChange}
                        name="firstName"
                        id="firstName"
                      />
                    </div>
                    <div>
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        Last Name &nbsp;
                        {roleInputError?.lastName && (
                          <small style={{ color: "red" }}>
                            {"Last Name is required"}
                          </small>
                        )}
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="text"
                        placeholder="Last Name"
                        value={roleInput?.lastName}
                        onChange={roleChange}
                        name="lastName"
                        id="lastName"
                      />
                    </div>

                    <div>
                      {" "}
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        Email &nbsp;
                        {emailValidation ? (
                          <small style={{ color: "red" }}>
                            {"Enter Valid Email"}
                          </small>
                        ) : (
                          roleInputError?.email && (
                            <small style={{ color: "red" }}>
                              {"Email is required"}
                            </small>
                          )
                        )}
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="email"
                        placeholder="abc@samsar.com"
                        value={roleInput?.email}
                        onChange={roleChange}
                        InputProps={{
                          readOnly: edit ? true : false,
                        }}
                        onBlur={(e) => handleValidate(e.target.value)}
                        name="email"
                        id="email"
                      />
                    </div>
                    <div>
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        Phone Number &nbsp;
                        {roleInputError?.phoneNumber && (
                          <small style={{ color: "red" }}>
                            {"Phone Number is required"}
                          </small>
                        )}
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="tel"
                        placeholder="Phone Number"
                        value={roleInput?.phoneNumber}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setroleInput({ ...roleInput, phoneNumber: digits });
                          setroleInputError({ ...roleInputError, phoneNumber: false });
                        }}
                        name="phoneNumber"
                        id="phoneNumber"
                        inputProps={{
                          maxLength: 10,
                          inputMode: "numeric",
                          onPaste: (e) => {
                            e.preventDefault();
                            const pasted = e.clipboardData.getData("text");
                            const digits = pasted.replace(/\D/g, "").slice(0, 10);
                            setroleInput({ ...roleInput, phoneNumber: digits });
                            setroleInputError({ ...roleInputError, phoneNumber: false });
                          },
                        }}
                      />
                    </div>
                    {/* {!edit ? (
                      <div>
                        <InputLabel
                          id="demo-simple-select-label"
                          style={{ marginLeft: "7px" }}
                        >
                          Password &nbsp;
                          {roleInputError?.password && (
                            <small style={{ color: "red" }}>
                              {"password is required"}
                            </small>
                          )}
                        </InputLabel>
                        <TextField
                          variant="outlined"
                          style={{
                            // width: "370px",
                            marginLeft: "5px",
                            height: "100%",
                          }}
                          className="form-control"
                          type="password"
                          placeholder="******"
                          value={roleInput?.password}
                          onChange={roleChange}
                          name="password"
                          id="password"
                        />
                      </div>
                    ) : (
                      ""
                    )} */}
                  </div>
                  <hr />
                  <h4 style={{ margin: "10px" }}>Risk Assessment</h4>
                  <div style={{ margin: "10px" }} className="page-grid">
                    <div>
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        Risk Score &nbsp;
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="number"
                        placeholder="Risk Score"
                        value={roleInput?.riskAssessment?.score}
                        // onChange={roleChange}
                        name="score"
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                          if (
                            e.key === "-" ||
                            e.key === "+" ||
                            e.key === "e" ||
                            e.key === "E"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = +sanitizeInput(e.target.value);
                          if (value >= 0) {
                            setroleInput({
                              ...roleInput,
                              riskAssessment: {
                                ...roleInput.riskAssessment,
                                score: value,
                              },
                            });
                          }
                        }}
                      />
                    </div>
                    <div>
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        Location Risk &nbsp;
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="number"
                        placeholder="Location Risk"
                        value={roleInput?.riskAssessment?.location}
                        name="location"
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                          if (
                            e.key === "-" ||
                            e.key === "+" ||
                            e.key === "e" ||
                            e.key === "E"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = +sanitizeInput(e.target.value);
                          if (value >= 0) {
                            setroleInput({
                              ...roleInput,
                              riskAssessment: {
                                ...roleInput.riskAssessment,
                                location: value,
                              },
                            });
                          }
                        }}
                      />
                    </div>

                    <div>
                      {" "}
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        PEP Risk &nbsp;
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="number"
                        placeholder="Pep Risk"
                        value={roleInput?.riskAssessment?.pepStatus}
                        name="pepStatus"
                        id="pepStatus"
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                          if (
                            e.key === "-" ||
                            e.key === "+" ||
                            e.key === "e" ||
                            e.key === "E"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = +sanitizeInput(e.target.value);
                          if (value >= 0) {
                            setroleInput({
                              ...roleInput,
                              riskAssessment: {
                                ...roleInput.riskAssessment,
                                pepStatus: value,
                              },
                            });
                          }
                        }}
                      />
                    </div>
                    <div>
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        Age Risk &nbsp;
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="number"
                        placeholder="Age Risk"
                        value={roleInput?.riskAssessment?.age}
                        name="age"
                        id="age"
                      />
                    </div>
                    <div>
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        Age Risk &nbsp;
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="number"
                        placeholder="Age Risk"
                        value={roleInput?.riskAssessment?.age}
                        name="age"
                        id="age"
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                          if (
                            e.key === "-" ||
                            e.key === "+" ||
                            e.key === "e" ||
                            e.key === "E"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = +sanitizeInput(e.target.value);
                          if (value >= 0) {
                            setroleInput({
                              ...roleInput,
                              riskAssessment: {
                                ...roleInput.riskAssessment,
                                age: value,
                              },
                            });
                          }
                        }}
                      />
                    </div>
                    <div>
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        Income Risk &nbsp;
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="number"
                        placeholder="Income Risk"
                        value={roleInput?.riskAssessment?.income}
                        name="income"
                        id="income"
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                          if (
                            e.key === "-" ||
                            e.key === "+" ||
                            e.key === "e" ||
                            e.key === "E"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = +sanitizeInput(e.target.value);
                          if (value >= 0) {
                            setroleInput({
                              ...roleInput,
                              riskAssessment: {
                                ...roleInput.riskAssessment,
                                income: value,
                              },
                            });
                          }
                        }}
                      />
                    </div>
                    <div>
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        Occupation Risk &nbsp;
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="number"
                        placeholder="Occupation Risk"
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                          if (
                            e.key === "-" ||
                            e.key === "+" ||
                            e.key === "e" ||
                            e.key === "E"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = +sanitizeInput(e.target.value);
                          if (value >= 0) {
                            setroleInput({
                              ...roleInput,
                              riskAssessment: {
                                ...roleInput.riskAssessment,
                                occupation: value,
                              },
                            });
                          }
                        }}
                      />
                    </div>
                    <div>
                      <InputLabel
                        id="demo-simple-select-label"
                        style={{ marginLeft: "7px" }}
                      >
                        IFSC Code Risk &nbsp;
                      </InputLabel>
                      <TextField
                        variant="outlined"
                        style={{
                          // width: "370px",
                          marginLeft: "5px",
                          height: "100%",
                        }}
                        className="form-control"
                        type="number"
                        placeholder="IFSC Risk"
                        value={roleInput?.riskAssessment?.ifsc}
                        name="ifsc"
                        id="ifsc"
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                          if (
                            e.key === "-" ||
                            e.key === "+" ||
                            e.key === "e" ||
                            e.key === "E"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = +sanitizeInput(e.target.value);
                          if (value >= 0) {
                            setroleInput({
                              ...roleInput,
                              riskAssessment: {
                                ...roleInput.riskAssessment,
                                ifsc: value,
                              },
                            });
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="list-layout">
                  {alertMode && (
                    <div style={{
                      background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 8,
                      padding: "10px 16px", marginBottom: 14, fontSize: 13, color: "#5d4037",
                      display: "flex", alignItems: "center", gap: 10,
                    }}>
                      <span style={{ fontSize: 18 }}>⚠</span>
                      <div>
                        <strong>User Alerts mode</strong> — showing users with high risk score (≥4),
                        high KYC retry count (≥2), or low selfie confidence (≤70%).
                        Location columns (Country Code, State, City) are auto-visible.
                      </div>
                    </div>
                  )}
                  <div className="list-layout__main">
                    <div className="card">
                      <div className="card-body">
                        <TableToolbar
                          pageSize={pageSize}
                          setPageSize={setPageSize}
                          pageSizeOptions={pageSizeOptions}
                          total={userList?.total}
                          page={page}
                          visibleColumnKeys={visibleColumnKeys}
                          allColumns={ALL_COLUMNS}
                          toggleColumn={toggleColumn}
                          resetColumns={resetColumns}
                          onDownload={handleDownload}
                          downloadLoading={downloadLoading}
                        />

                        <div className="table-responsive">
                          {loading ? (
                            <div className="his-spin">
                              <Spin
                                size="large"
                                style={{ position: "relative", top: "35%" }}
                              />
                            </div>
                          ) : (
                            <Table
                              pagination={false}
                              style={{ overflowX: "auto" }}
                              columns={columns2}
                              dataSource={userList ? userList?.result : []}
                              rowKey={(record) => record._id || record.id}
                              onRow={(record) => ({
                                onClick: () => {
                                  if (record?._id) {
                                    history.push(
                                      `/admin/user/profile/${record._id}`,
                                    );
                                  }
                                },
                                style: { cursor: "pointer" },
                              })}
                            />
                          )}
                          {userList && userList?.total > pageSize && (
                            <CustomPagination
                              key={`pg-${pageSize}`}
                              totalTabs={userList?.total}
                              tabsPerPage={pageSize}
                              handleChange={setPage}
                              className={`${
                                loading ? "display-hide" : "display-flex"
                              }`}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="list-layout__filters">
                    <FilterPanel
                      count={userList?.total}
                      onClear={() => {
                        setStartDate();
                        setEndDate();
                        setFilter("");
                        setSearchTerm("");
                        setKyc(undefined);
                        setBankStatus("");
                        setComplyFilter("");
                        setFreezeFilter("");
                        handleRiskOptionChange(null);
                        setFilterUserId("");
                        setFilterMobile("");
                        setFilterEmail("");
                      }}
                    >
                      <FilterPanel.Field label="Search">
                        <input
                          type="text"
                          placeholder="Name or email"
                          value={searchTerm}
                          onChange={(e) =>
                            setSearchTerm(sanitizeInput(e.target.value))
                          }
                        />
                      </FilterPanel.Field>

                      {/* <FilterPanel.Field label="Email">
                        <input
                          type="text"
                          placeholder="Filter by email"
                          value={filterEmail}
                          onChange={(e) =>
                            setFilterEmail(sanitizeInput(e.target.value))
                          }
                        />
                      </FilterPanel.Field> */}

                      <FilterPanel.Field label="User ID">
                        <input
                          type="text"
                          placeholder="Enter User ID"
                          value={filterUserId}
                          onChange={(e) =>
                            setFilterUserId(sanitizeInput(e.target.value))
                          }
                        />
                      </FilterPanel.Field>

                      <FilterPanel.Field label="Mobile Number">
                        <input
                          type="text"
                          placeholder="Enter mobile number"
                          value={filterMobile}
                          onChange={(e) =>
                            setFilterMobile(sanitizeInput(e.target.value))
                          }
                        />
                      </FilterPanel.Field>

                      <FilterPanel.Field label="KYC Status">
                        <Select
                          options={kycOptions}
                          value={
                            kycOptions.find((opt) => opt.value === kyc) || null
                          }
                          onChange={(selected) => setKyc(selected?.value)}
                          styles={customStyles}
                          placeholder="Any"
                          isClearable
                        />
                      </FilterPanel.Field>

                      <FilterPanel.Field label="Bank Verification">
                        <Select
                          options={bankVerificationOptions}
                          value={
                            bankVerificationOptions.find(
                              (opt) => opt.value === bankStatus
                            ) || null
                          }
                          onChange={(selected) =>
                            setBankStatus(selected?.value || "")
                          }
                          styles={customStyles}
                          placeholder="Any"
                          isClearable
                        />
                      </FilterPanel.Field>

                      <FilterPanel.Field label="ComplyAdvantage">
                        <Select
                          options={complyAdvantageOptions}
                          value={
                            complyAdvantageOptions.find(
                              (opt) => opt.value === complyFilter
                            ) || null
                          }
                          onChange={(selected) =>
                            setComplyFilter(selected?.value || "")
                          }
                          styles={customStyles}
                          placeholder="Any"
                          isClearable
                        />
                      </FilterPanel.Field>

                      <FilterPanel.Field label="Freeze Status">
                        <Select
                          options={freezeOptions}
                          value={
                            freezeOptions.find(
                              (opt) => opt.value === freezeFilter
                            ) || null
                          }
                          onChange={(selected) =>
                            setFreezeFilter(selected?.value || "")
                          }
                          styles={customStyles}
                          placeholder="Any"
                          isClearable
                        />
                      </FilterPanel.Field>

                      <FilterPanel.Field label="User Status">
                        <Select
                          options={userStatusOptions}
                          value={
                            userStatusOptions.find(
                              (opt) => opt.value === filter
                            ) || null
                          }
                          onChange={(selected) => setFilter(selected?.value)}
                          styles={customStyles}
                          placeholder="Any"
                          isClearable
                        />
                      </FilterPanel.Field>

                      <FilterPanel.Field label="Risk Level">
                        <Select
                          options={riskOptions}
                          value={
                            riskOptions.find(
                              (opt) => opt.value === riskOption
                            ) || null
                          }
                          onChange={(selected) =>
                            handleRiskOptionChange(selected)
                          }
                          styles={customStyles}
                          placeholder="Any"
                          isClearable
                        />
                      </FilterPanel.Field>

                      <FilterPanel.Field label="Date Range">
                        <DateRangePicker
                          onApply={handleDateRangeChange}
                          isClearable={true}
                        >
                          <input
                            type="text"
                            placeholder="Select date range"
                            readOnly
                          />
                        </DateRangePicker>
                      </FilterPanel.Field>
                    </FilterPanel>
                  </div>
                </div>
              )}
            </div>
          </div>
          <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={openDelete}
            onClose={handleCloseDelete}
            closeAfterTransition
            slots={{ backdrop: backdrop }}
            slotProps={{
              backdrop: {
                timeout: 500,
              },
            }}
          >
            <Fade in={openDelete}>
              <Box style={styleDelete}>
                <div style={{ margin: "10px 10px 6px", textAlign: "center" }}>
                  <div style={{ fontSize: "16px", fontWeight: 600, marginBottom: "8px" }}>
                    Delete User
                  </div>
                  <div style={{ fontSize: "13px", color: "#555", marginBottom: "4px" }}>
                    Are you sure you want to delete this user? This action cannot be undone.
                  </div>
                  {id && (
                    <div style={{
                      background: "#f8f8f8",
                      borderRadius: "6px",
                      padding: "8px 10px",
                      marginTop: "10px",
                      textAlign: "left",
                      fontSize: "13px",
                    }}>
                      <div><strong>Name:</strong> {`${id?.firstName ?? ""} ${id?.lastName ?? ""}`.trim() || "-"}</div>
                      <div><strong>Email:</strong> {id?.email || "-"}</div>
                    </div>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <button
                    className="btn btn-danger"
                    disabled={loading}
                    onClick={handleDeleteFunction}
                    style={{ marginBottom: "10px", marginTop: "15px" }}
                  >
                    {loading ? <Spinner size="sm" /> : "Delete"}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleCloseDelete}
                    style={{
                      marginLeft: "7px",
                      marginBottom: "10px",
                      marginTop: "15px",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </Box>
            </Fade>
          </Modal>
          {/* Reactivate confirmation modal */}
          <Modal
            open={openReactivate}
            onClose={handleCloseReactivate}
            closeAfterTransition
            slots={{ backdrop: backdrop }}
            slotProps={{ backdrop: { timeout: 500 } }}
          >
            <Fade in={openReactivate}>
              <Box style={styleDelete}>
                <div style={{ margin: "10px", textAlign: "center" }}>
                  Reactivate this user's account?
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <button
                    className="btn btn-success"
                    disabled={loading}
                    onClick={handleReactivateFunction}
                    style={{ marginBottom: "10px", marginTop: "15px" }}
                  >
                    {loading ? <Spinner size="sm" /> : "Reactivate"}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleCloseReactivate}
                    style={{ marginLeft: "7px", marginBottom: "10px", marginTop: "15px" }}
                  >
                    Cancel
                  </button>
                </div>
              </Box>
            </Fade>
          </Modal>

          {/* Deactivate / Activate confirmation modal */}
          <Modal
            open={openDeactivate}
            onClose={handleCloseDeactivate}
            closeAfterTransition
            slots={{ backdrop: backdrop }}
            slotProps={{ backdrop: { timeout: 500 } }}
          >
            <Fade in={openDeactivate}>
              <Box style={styleDelete}>
                <div style={{ margin: "10px", textAlign: "center", fontWeight: 600 }}>
                  {id?.isDeactivated
                    ? "Activate this user's account?"
                    : "Deactivate this user's account?"}
                </div>
                <div style={{ margin: "10px", textAlign: "center", fontSize: 13, color: "#555" }}>
                  {id?.isDeactivated
                    ? "The user will be able to log in again."
                    : "Once deactivated, the user will not be able to log in. They will need to raise a support ticket to reactivate the account."}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <button
                    className={id?.isDeactivated ? "btn btn-success" : "btn btn-warning"}
                    disabled={loading}
                    onClick={handleDeactivateConfirm}
                    style={{ marginBottom: "10px", marginTop: "15px" }}
                  >
                    {loading ? <Spinner size="sm" /> : id?.isDeactivated ? "Activate" : "Deactivate"}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleCloseDeactivate}
                    style={{ marginLeft: "7px", marginBottom: "10px", marginTop: "15px" }}
                  >
                    Cancel
                  </button>
                </div>
              </Box>
            </Fade>
          </Modal>

          {/* Disable 2FA confirmation modal */}
          <Modal
            open={openDisable2FA}
            onClose={handleCloseDisable2FA}
            closeAfterTransition
            slots={{ backdrop: backdrop }}
            slotProps={{ backdrop: { timeout: 500 } }}
          >
            <Fade in={openDisable2FA}>
              <Box style={styleDelete}>
                <div style={{ margin: "10px", textAlign: "center" }}>
                  Disable 2FA for this user?
                </div>
                <p style={{ textAlign: "center", fontSize: "12px", color: "#666", margin: "0 10px" }}>
                  Use this only if the user has lost access to their authenticator app.
                </p>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <button
                    className="btn btn-danger"
                    disabled={loading}
                    onClick={handleDisable2FAFunction}
                    style={{ marginBottom: "10px", marginTop: "15px" }}
                  >
                    {loading ? <Spinner size="sm" /> : "Disable 2FA"}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleCloseDisable2FA}
                    style={{ marginLeft: "7px", marginBottom: "10px", marginTop: "15px" }}
                  >
                    Cancel
                  </button>
                </div>
              </Box>
            </Fade>
          </Modal>

          {/* Block / Unblock confirmation modal with mandatory reason */}
          <Modal
            open={openBlock}
            onClose={() => { setOpenBlock(false); setBlockReason(""); setBlockReasonError(false); }}
            closeAfterTransition
            slots={{ backdrop: backdrop }}
            slotProps={{ backdrop: { timeout: 500 } }}
          >
            <Fade in={openBlock}>
              <Box style={{ ...styleDelete, width: 360 }}>
                <div style={{ margin: "10px 10px 6px" }}>
                  <div style={{ fontSize: "16px", fontWeight: 700, marginBottom: 6, color: blockAction ? "#b71c1c" : "#2e7d32" }}>
                    {blockAction ? "Block User" : "Unblock User"}
                  </div>

                  {id && (
                    <div style={{ background: "#f8f8f8", borderRadius: 6, padding: "8px 10px", marginBottom: 12, fontSize: 13 }}>
                      <div><strong>Name:</strong> {`${id?.firstName ?? ""} ${id?.lastName ?? ""}`.trim() || "-"}</div>
                      <div><strong>Email:</strong> {id?.email || "-"}</div>
                    </div>
                  )}

                  {blockAction ? (
                    <>
                      <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>
                        Are you sure you want to block this user? They will lose access to trading and withdrawals.
                      </div>
                      <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>
                        Reason <span style={{ color: "#b71c1c" }}>*</span>
                      </label>
                      <textarea
                        rows={3}
                        placeholder="Enter reason for blocking this user (required)"
                        value={blockReason}
                        onChange={(e) => { setBlockReason(e.target.value); setBlockReasonError(false); }}
                        style={{
                          width: "100%", padding: "8px", borderRadius: 6, fontSize: 13,
                          border: blockReasonError ? "1px solid #b71c1c" : "1px solid #ccc",
                          outline: "none", resize: "vertical",
                        }}
                      />
                      {blockReasonError && (
                        <div style={{ color: "#b71c1c", fontSize: 12, marginTop: 3 }}>
                          Reason is required to block a user
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, color: "#555", marginBottom: 8 }}>
                        Are you sure you want to unblock this user? They will regain full access.
                      </div>
                      <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>
                        Reason (optional)
                      </label>
                      <textarea
                        rows={2}
                        placeholder="Enter reason for unblocking (optional)"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        style={{ width: "100%", padding: "8px", borderRadius: 6, fontSize: 13, border: "1px solid #ccc", outline: "none", resize: "vertical" }}
                      />
                    </>
                  )}
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, padding: "8px 10px" }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => { setOpenBlock(false); setBlockReason(""); setBlockReasonError(false); }}
                    style={{ fontSize: 13 }}
                  >
                    Cancel
                  </button>
                  <button
                    className={blockAction ? "btn btn-danger" : "btn btn-success"}
                    disabled={loading}
                    onClick={handleBlockConfirm}
                    style={{ fontSize: 13 }}
                  >
                    {loading ? <Spinner size="sm" /> : blockAction ? "Block User" : "Unblock User"}
                  </button>
                </div>
              </Box>
            </Fade>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default User;
